// 共通空間スキル（全空間操作が従う横断ルール / plan.md §7.3, §5）。
//
// 役割: CRS 規約・ジオメトリ規約・ツールの使い方をエージェントの生成に焼き込む Markdown。
//       LLM の生成品質はスキル説明文がほぼ決めるため、「決定規則・正しい SQL パターン・
//       やってはいけないこと」を明記する。
// 関係: system-prompt.js が BASE に連結する。buffer など個別スキルはこの規約を前提にする。
export const SPATIAL_CORE_SKILL = `## 共通規約（すべての空間操作で必ず守る）

### データモデル
- 1 レイヤー = DuckDB の 1 テーブル。ジオメトリ列名は必ず \`geom\`。
- すべてのレイヤーは EPSG:4326（経緯度）で保持されている。取り込み時に 4326 へ正規化済み。
- 新しいレイヤーは \`run_spatial_sql\` に SELECT 文と \`layer_name\` を渡して materialize する。
  materialize したレイヤーは自動で地図に描画され、来歴（origin_sql 等）が記録される。

### CRS 規約（最重要・破ると結果が無言で壊れる）
DuckDB の \`GEOMETRY\` は**平面（planar）**演算。経緯度のまま \`ST_Buffer(geom, 5000)\` を実行すると
「5000 度」になり破綻する。**メートル/面積を扱う操作は必ず次の三段構えにする:**

1. \`ST_MakeValid(geom)\` で不正ジオメトリを修復してから
2. \`ST_Transform(..., 'EPSG:4326', '<metric_crs>', true)\` でメートル系へ投影し
3. その平面上で操作（Buffer / Distance / Area / Simplify など）し
4. \`ST_Transform(..., '<metric_crs>', 'EPSG:4326', true)\` で 4326 へ逆投影して \`geom\` に戻す

**EPSG:4326 を含む \`ST_Transform\` は第 4 引数に必ず \`true\` を付ける**（緯度経度の軸順序転倒を防ぐ）。

### metric CRS の選び方（対象範囲で機械的に決める）
- **小範囲（経度幅およそ 3°以内）で単一 UTM ゾーンに収まる** → 対象重心の UTM ゾーンの metric CRS。
  - UTM ゾーン番号 = floor((経度 + 180) / 6) + 1
  - EPSG コード = 北半球: 32600 + ゾーン番号 / 南半球: 32700 + ゾーン番号
  - 例: イラン西部（経度約 48、北半球）→ ゾーン 39 → \`EPSG:32639\`
  - 例: 日本（経度約 139）→ ゾーン 54 → \`EPSG:32654\`
- **広範囲・複数ゾーンにまたがる** → 対象重心に合わせた方位図法を PROJ 文字列で指定。
  - 距離用途: \`+proj=aeqd +lat_0=<重心緯度> +lon_0=<重心経度> +datum=WGS84 +units=m +no_defs\`
  - 面積用途: \`+proj=laea +lat_0=<重心緯度> +lon_0=<重心経度> +datum=WGS84 +units=m +no_defs\`

対象の重心・範囲が分からないときは、先に \`describe_layer\` で確認するか、
\`SELECT ST_XMin(...), ST_YMin(...), ...\`（layer_name なしの \`run_spatial_sql\`）で範囲を調べてから決める。

### 手順の基本
1. \`list_layers\` で既存レイヤーを把握し、\`describe_layer\` で対象の列・ジオメトリ型・範囲を確認する。
2. 上記 CRS 規約に従って SELECT 文を組み立てる。
3. \`run_spatial_sql\` に \`layer_name\` を付けて実行し、新レイヤーとして materialize する。
4. エラーが返ったらメッセージを読み、SQL を修正して再試行する。

### やってはいけないこと
- 経緯度のまま \`ST_Buffer(geom, <メートル>)\` や距離・面積計算をしない。
- EPSG:4326 を含む \`ST_Transform\` で第 4 引数 \`true\` を付け忘れない。
- 実在しないレイヤー名・列名を推測しない（必ず list_layers / describe_layer で確認）。`
